import { writable, get } from "svelte/store";
import { context, SpanStatusCode, trace } from "@opentelemetry/api";
import { buildSpanNameFromAttribs, getStandardAttributes } from "$lib/utilities/opentelemetry";
import logger from "$lib/utilities/logger";

export const tracer = trace.getTracer(import.meta.env.VITE_TELEMETRY_SERVICE_NAME);

const createPageSpanStore = () => {
  const store = writable({ span: null, path: null, viewKey: null });
  const { subscribe, set } = store;
  let current = { span: null, path: null, viewKey: null };

  return {
    subscribe,
    clear: () => {
      current.span?.end();
      current = { span: null, path: null, viewKey: null };
      set(current);
    },
    // attribs is an object: e.g. { path, viewKey, url, ... }
    initialize: (attribs = {}) => {
      const attrs = getStandardAttributes(attribs); // auto "of." here

      const span = tracer.startSpan(
        buildSpanNameFromAttribs("Page Execution", attribs), // builder prefixes internally
        { attributes: attrs },
        context.active()
      );
      current = {
        span,
        path: attrs["of.path"] ?? null,
        viewKey: attrs["of.viewKey"] ?? null
      };
      set(current);
    },
    setViewKey: (viewKey) => {
      if (current.span?.isRecording?.()) {
        current.span.updateName(`Page Execution ${viewKey}`);
        current.span.setAttribute("of.viewKey", viewKey);
      }
      current = { ...current, viewKey };
      set(current);
    },
    /** Create a short child span of the current page span */
    withChild: async (name, fn, attrs = {}) => {
      const parent = get(store) || current;
      const parentCtx = parent.span
        ? trace.setSpan(context.active(), parent.span)
        : context.active();

      // Ensure path/viewKey present; auto "of." happens inside getStandardAttributes
      const startAttrs = getStandardAttributes({
        ...attrs,
        path: parent.path,
        ...(parent.viewKey ? { viewKey: parent.viewKey } : {})
      });

      return tracer.startActiveSpan(
        `${name}${parent.viewKey ? `: ${parent.viewKey}` : ""}`,
        { attributes: startAttrs },
        parentCtx,
        async (span) => {
          try {
            let result = await fn(span);
            span.setStatus(SpanStatusCode.OK);
            return result;
          } catch (e) {
            span.setStatus(SpanStatusCode.ERROR);
            logger.error(`Error in child span ${name}: ${e.message}`);
          } finally {
            span.end();
          }
        }
      );
    }
  };
};

const pageSpanStore = createPageSpanStore();
export default pageSpanStore;
